import { IdentificationOx } from '@/query';
import Dexie, { Table } from 'dexie';

export type SyncIdentificationSteps =
  | 'identification'
  | 'change-characteristic'
  | 'add-to-history'
  | 'delete'
  | 'error';

export interface SyncIdentificationData extends IdentificationOx {
  sisbovNumber?: string;
  origin: number;
  breedBND: string | undefined;
  managementId: string;
  gtaId: number;
  oxId: number;
}

export interface SyncIdentificationManagementData {
  id?: number;
  data: SyncIdentificationData;
  sisbov: number | undefined | null;
  electronic: string | undefined | null;
  gtaId: number;
  timestamp: number;
  retryCount: number;
  oxId: number;
  status: 'pending' | 'processing' | 'failed' | 'success' | 'other';
  step: SyncIdentificationSteps;
  error?: string;
}

export interface SyncIdentificationManagementDataAuthorizedDeletion {
  id?: number;
  timestamp: number;
  data: SyncIdentificationData;
}

export class SyncIdentificationDexieService extends Dexie {
  pending!: Table<SyncIdentificationManagementData>;
  managed!: Table<SyncIdentificationManagementData>;
  authorizedDeletion!: Table<SyncIdentificationManagementDataAuthorizedDeletion>;

  constructor() {
    super('feed:sync-identification-management');
    this.version(3)
      .stores({
        pending: '++id, timestamp, status, sisbov, gtaId',
        managed: '++id, timestamp, status, sisbov, gtaId',
        authorizedDeletion: '++id, timestamp'
      })
      .upgrade(async (tx) => {
        await tx.table('pending').toCollection().modify({
          sisbov: null
        });
      });

    this.version(4).stores({
      pending: '++id, timestamp, status, sisbov, electronic, gtaId',
      managed: '++id, timestamp, status, sisbov, electronic, gtaId',
      authorizedDeletion: '++id, timestamp'
    });
  }

  // ----------------------------------------------------------------------

  async addPendingData(data: SyncIdentificationData): Promise<number> {
    await this.managed.add({
      data,
      sisbov: data.sisbov,
      electronic: data.electronic ?? null,
      gtaId: data.gtaId,
      oxId: data.oxId,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      step: 'identification'
    });

    return await this.pending.add({
      data,
      sisbov: data.sisbov,
      electronic: data.electronic ?? null,
      gtaId: data.gtaId,
      oxId: data.oxId,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      step: 'identification'
    });
  }

  async addOthersData(data: SyncIdentificationData): Promise<number> {
    await this.managed.add({
      data,
      sisbov: data.sisbov,
      electronic: data.electronic ?? null,
      gtaId: data.gtaId,
      oxId: data.oxId,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      step: 'identification'
    });

    return await this.pending.add({
      data,
      sisbov: data.sisbov,
      electronic: data.electronic ?? null,
      gtaId: data.gtaId,
      oxId: data.oxId,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'other',
      step: 'identification'
    });
  }

  async addAuthorizedDeletionData(data: SyncIdentificationData): Promise<void> {
    await this.authorizedDeletion.add({
      data,
      timestamp: Date.now()
    });
  }

  // ----------------------------------------------------------------------

  async getPendingData(limit: number = 10, getAll: boolean = false): Promise<SyncIdentificationManagementData[]> {
    if (getAll) {
      const pending = await this.pending.where('status').equals('pending').limit(limit).toArray();
      const processing = await this.pending.where('status').equals('processing').limit(limit).toArray();

      return [...pending, ...processing];
    }

    return await this.pending.where('status').equals('pending').limit(limit).toArray();
  }

  async getSuccessData(): Promise<SyncIdentificationManagementData[]> {
    const success = await this.pending.where('status').equals('success').toArray();
    const pending = await this.pending.where('status').equals('pending').toArray();
    const processing = await this.pending.where('status').equals('processing').toArray();
    const others = await this.pending.where('status').equals('other').toArray();

    return [...success, ...pending, ...processing, ...others];
  }

  async getAllData(): Promise<SyncIdentificationManagementData[]> {
    return await this.pending.toArray();
  }

  async getAuthorizedDeletionData(): Promise<SyncIdentificationManagementDataAuthorizedDeletion[]> {
    return await this.authorizedDeletion.toArray();
  }

  async getManagedData(): Promise<SyncIdentificationManagementData[]> {
    return await this.managed.toArray();
  }

  // ----------------------------------------------------------------------

  async updateDataStatus(
    id: number,
    status: SyncIdentificationManagementData['status'],
    error?: string
  ): Promise<void> {
    await this.pending.update(id, { status, error });
  }

  async updateSisbovStatus(sisbov: number, status: SyncIdentificationManagementData['status']): Promise<void> {
    await this.pending.where('sisbov').equals(sisbov).modify({ status });
  }

  async updateSisbovOnConfirmation(
    sisbov: number,
    oxId: number,
    status: SyncIdentificationManagementData['status'] = 'other'
  ): Promise<void> {
    await this.pending
      .where('sisbov')
      .equals(sisbov)
      .modify((row) => {
        row.status = status;
        row.oxId = oxId;
        row.data.oxId = oxId;
      });
  }

  async findSisbov(sisbov: number): Promise<SyncIdentificationManagementData | undefined> {
    return await this.pending.where('sisbov').equals(sisbov).first();
  }

  async findElectronic(electronic: string): Promise<SyncIdentificationManagementData | undefined> {
    return await this.pending.where('electronic').equals(electronic).first();
  }

  async updateElectronicOnConfirmation(
    electronic: string,
    oxId: number,
    status: SyncIdentificationManagementData['status'] = 'other'
  ): Promise<void> {
    await this.pending
      .where('electronic')
      .equals(electronic)
      .modify((row) => {
        row.status = status;
        row.oxId = oxId;
        row.data.oxId = oxId;
      });
  }

  async deleteSisbov(sisbov: number, callback: () => void): Promise<void> {
    const other = await this.pending.where('status').equals('other').toArray();

    if (other.length === 1) {
      callback();
    }

    try {
      await this.pending.where('sisbov').equals(sisbov).delete();
    } catch (error) {
      console.warn('Não foi encontrado o sisbov em memória:', sisbov);
    }
  }

  async deleteOtherBySisbov(sisbov: number): Promise<void> {
    try {
      await this.pending
        .where('sisbov')
        .equals(sisbov)
        .and((row) => row.status === 'other')
        .delete();
    } catch (error) {
      console.error('Erro ao deletar registro other:', error);
    }
  }

  async deleteOtherByElectronic(electronic: string): Promise<void> {
    try {
      await this.pending
        .where('electronic')
        .equals(electronic)
        .and((row) => row.status === 'other')
        .delete();
    } catch (error) {
      console.error('Erro ao deletar registro other:', error);
    }
  }

  async updateDataStep(id: number, step: SyncIdentificationSteps): Promise<void> {
    await this.pending.update(id, { step });
  }

  async updateOxStep(id: number, oxId: number, step: SyncIdentificationSteps): Promise<void> {
    await this.pending.update(id, { step, oxId });
  }

  async getStep(id: number): Promise<SyncIdentificationSteps> {
    const item = await this.pending.get(id);

    if (!item) {
      throw new Error(`Item com ID ${id} não encontrado.`);
    }

    return item.step;
  }

  async incrementRetryCount(id: number): Promise<void> {
    const item = await this.pending.get(id);

    if (item) {
      await this.pending.update(id, {
        retryCount: item.retryCount + 1,
        status: 'pending'
      });
    }
  }

  async deleteData(id: number): Promise<void> {
    try {
      await this.pending.delete(id);
    } catch (error) {
      console.warn('Não foi encontrado o id em memória:', id);
    }
  }

  async deleteAuthorizedData(_: number, id: number) {
    try {
      // await this.pending.where('sisbov').equals(sisbov).delete();
      await this.authorizedDeletion.delete(id);
    } catch (error) {
      console.warn('Não foi encontrado o id em memória:', id);
    }
  }

  async getFailedData(): Promise<SyncIdentificationManagementData[]> {
    return await this.pending.where('status').equals('failed').toArray();
  }
}
